<template>
  <Modal title="Sequence advance" :max-width="560" @close="onClose">
    <div class="ss-body">
      <p class="ss-prose">{{ sentence }}</p>

      <p v-if="skipped.length" class="ss-skipped">
        Walked past {{ skipped.length }} step{{ skipped.length === 1 ? '' : 's' }}:
        {{ skipped.map((s) => `${s.step_index} (${s.label})`).join(', ') }}
      </p>

      <div class="ss-at">{{ timestamp }}</div>

      <div class="ss-pills">
        <button v-if="sequenceTarget" class="ss-pill" @click="go(sequenceTarget)">
          <AppIcon name="sequence" :size="12" />
          {{ data.sequence_name }}
        </button>
        <button v-if="data.sales_play_id" class="ss-pill" @click="go(`/sales-plays/${data.sales_play_id}`)">
          <AppIcon name="sales_play" :size="12" />
          {{ data.sales_play_name }}
        </button>
        <button v-if="data.reachout_id" class="ss-pill" @click="go(`/actions/${data.reachout_id}`)">
          <AppIcon :name="reachoutIcon" :size="12" />
          {{ reachoutLabel }}
        </button>
      </div>
    </div>

    <template #footer>
      <button class="btn btn-secondary" @click="onClose">Close</button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import Modal from '../../next/components/Modal.vue'
import AppIcon from '../../components/AppIcon.vue'

const props = defineProps<{
  data: Record<string, any>
  createdAt?: string | null
  onClose?: () => void
}>()

const router = useRouter()
const data = computed(() => props.data || {})

const skipped = computed(() => data.value.skipped_steps || [])

/**
 * The step labels come from the enrollment's config snapshot, so they are
 * absent for enrollments deleted since the transition was written.
 */
const sentence = computed(() => {
  const from = data.value.from_step_index
  const to = data.value.to_step_index
  const fromLabel = data.value.from_step_label
  const toLabel = data.value.to_step_label
  const fromPart = fromLabel ? `step ${from} (${fromLabel})` : `step ${from}`
  const toPart = toLabel ? `step ${to} (${toLabel})` : `step ${to}`
  return `Advanced from ${fromPart} → ${toPart}`
})

const sequenceTarget = computed(() => {
  if (!data.value.sequence_name) return ''
  return data.value.sales_play_id
    ? `/sales-plays/${data.value.sales_play_id}/sequence`
    : `/sequences/${data.value.sequence_id}`
})

const REACHOUT_ICONS: Record<string, string> = {
  reachout_email: 'email',
  reachout_voicemail: 'voicemail',
  reachout_call: 'phone_call',
  reachout_linkedin_connect: 'linkedin',
}

const REACHOUT_LABELS: Record<string, string> = {
  reachout_email: 'Email',
  reachout_voicemail: 'Voicemail',
  reachout_call: 'Call',
  reachout_linkedin_connect: 'LinkedIn connect',
}

const reachoutIcon = computed(() => REACHOUT_ICONS[data.value.reachout_type] || 'timeline')

const reachoutLabel = computed(() => {
  const base = REACHOUT_LABELS[data.value.reachout_type] || 'Reachout'
  return data.value.reachout_status ? `${base} · ${data.value.reachout_status}` : base
})

const timestamp = computed(() => {
  if (!props.createdAt) return ''
  const d = new Date(props.createdAt)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString([], {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
})

function onClose() {
  props.onClose?.()
}

function go(path: string) {
  router.push(path)
  onClose()
}
</script>

<style scoped>
.ss-body { display: flex; flex-direction: column; gap: 10px; }
.ss-prose { margin: 0; font-size: 15px; color: #111827; }
.ss-skipped { margin: 0; font-size: 13px; color: #9ca3af; }
.ss-at { font-size: 13px; color: #6b7280; }
.ss-pills { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 4px; }
.ss-pill {
  display: inline-flex; align-items: center; gap: 6px;
  max-width: 100%;
  padding: 5px 10px;
  border: 1px solid #e5e7eb; border-radius: 999px;
  background: #f9fafb; color: #374151;
  font-size: 13px; cursor: pointer;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.ss-pill:hover { background: #f3f4f6; border-color: #d1d5db; color: #111827; }
</style>
